/**
 * The activity row list, shared by the item card's Activity tab and the board log panel
 * (`specs/M1-B8-activity-log.md` §2.3).
 *
 * Row shape, measured: `time · actor · entity · icon + label · before › after`, on a **56 px
 * pitch**, with an `Undo` button on the revertible rows and a deleted entity's name in orange
 * `[ic§Board-level activity log]`. Undo itself is q258 — the button renders disabled with the
 * reason on its title rather than lying about being available.
 */
import { relativeTime } from './composer-model';
import type { CardActivityRow } from './types';

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

export function ActivityRows({
  rows,
  emptyLabel = 'No activity yet',
  testId = 'activity-rows',
}: {
  rows: readonly CardActivityRow[];
  emptyLabel?: string;
  testId?: string;
}) {
  // A source that answers with nothing must render the empty state, never throw: the panel is
  // mounted by a URL segment and a throw there unmounts the board behind it (M0-F4 N2).
  const safe = Array.isArray(rows) ? rows : [];
  if (safe.length === 0) {
    return (
      <p className="ez-activity__empty" data-testid={`${testId}-empty`}>
        {emptyLabel}
      </p>
    );
  }
  return (
    <ul className="ez-activity__list" data-testid={testId}>
      {safe.map((row) => (
        <li key={row.id} className="ez-activity__row" data-testid={`activity-row-${row.id}`} data-event={row.event}>
          <time className="ez-activity__time" dateTime={row.createdAt}>
            {relativeTime(row.createdAt)}
          </time>
          <span className="ez-activity__avatar" aria-hidden="true">
            {initials(row.user?.name ?? '?')}
          </span>
          <span
            className={`ez-activity__entity${row.rendered.deleted ? ' is-deleted' : ''}`}
            style={row.rendered.color ? { color: row.rendered.color } : undefined}
            data-testid={`activity-row-${row.id}-entity`}
          >
            {row.rendered.entityName}
          </span>
          <span className="ez-activity__label" data-testid={`activity-row-${row.id}-label`}>
            {row.rendered.label}
          </span>
          <span className="ez-activity__values">
            {row.rendered.before === null && row.rendered.after === null ? (
              <span className="ez-activity__detail">{row.rendered.detail ?? ''}</span>
            ) : (
              <>
                <span className="ez-activity__before">{row.rendered.before ?? '-'}</span>
                <span className="ez-activity__arrow" aria-hidden="true">
                  ›
                </span>
                <span className="ez-activity__after">{row.rendered.after ?? '-'}</span>
              </>
            )}
          </span>
          {row.rendered.revertible ? (
            <button
              type="button"
              className="ez-activity__undo"
              disabled
              title="Undo needs a per-event inverse (open question q258)"
              data-testid={`activity-row-${row.id}-undo`}
            >
              Undo
            </button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
